<script>
import Sortable from 'sortablejs';
import { h } from 'vue';
import draggable from 'vuedraggable';

/**
 * Drag-to-reorder list container. Renders each item in a sortable region and emits the reordered
 * array via `v-model`; the consumer persists the new order.
 *
 * Two pickup models:
 *  - default (handle-scoped): only an element carrying `handleSelector` starts a drag, so touch
 *    scrolling and row taps are never hijacked.
 *  - `longPressBody`: the recognizable Material reorder pattern — the handle drags immediately,
 *    while the row body starts a drag on long-press (touch) / after a movement threshold (desktop).
 *    A quick tap still fires the row's own click; an un-held drag still scrolls the list.
 *
 * SortableJS remains the entire drag engine. `longPressBody` adds only a per-gesture delay decision:
 * a capture-phase pointerdown (which runs before SortableJS's own handler) sets the drag `delay` to
 * 0 when the pointer is on the handle and to `longPressDelay` otherwise. `delayOnTouchOnly` keeps
 * desktop immediate (with `fallbackTolerance` as the click-vs-drag threshold).
 *
 * Uses a render function to forward the consumer's `item` slot straight to vuedraggable by
 * reference; a template `<slot>` outlet would insert an extra fragment between vuedraggable and
 * each row, which breaks SortableJS handle detection.
 *
 * @status recommended
 * @group lists-tables
 * @useWhen Users manually reorder a short list (e.g. priority ordering)
 * @avoidWhen Order is derived or auto-sorted — reordering should reflect a real user-owned sequence
 * @slot item Row content for one element (`{ element, index }`); single root element with a `handleSelector` handle.
 * @a11y SortableJS drag is pointer/touch only — there is no keyboard affordance. Consumers MUST
 *   provide an accessible companion (e.g. "Move up" / "Move down" actions in each row's menu) for
 *   keyboard and screen-reader users.
 */
export default {
  name: 'HrSortableList',

  props: {
    /**
     * The ordered items. Reordering emits the new array order via `update:modelValue`.
     * @example [{"id":"a","label":"First"},{"id":"b","label":"Second"}]
     */
    modelValue: {
      type: Array,
      default: () => [],
    },
    /** Property name used as each item's stable key. */
    itemKey: {
      type: String,
      default: 'id',
    },
    /**
     * CSS selector for the drag handle within each row. In the default model, drag starts only
     * from a matching element. In `longPressBody`, the handle is the immediate-drag grip.
     */
    handleSelector: {
      type: String,
      default: '.hr-sortable-list__handle',
    },
    /**
     * Adds the row body as a long-press drag target on top of the immediate handle (the Material
     * reorder pattern). When false, only the handle drags.
     */
    longPressBody: {
      type: Boolean,
      default: false,
    },
    /** Long-press duration (ms) before a body drag starts on touch. Only used with `longPressBody`. */
    longPressDelay: {
      type: Number,
      default: 250,
    },
    /** Disables reordering; rows still render but cannot be dragged. */
    disabled: {
      type: Boolean,
      default: false,
    },
    /** Drag animation duration in milliseconds. */
    animation: {
      type: Number,
      default: 200,
    },
  },

  emits: {
    /** Emitted with the reordered array after a drag completes. Use with `v-model`. */
    'update:modelValue': null,
  },

  data() {
    return {
      // Internal writable mirror of modelValue. vuedraggable tracks drag context against a
      // stable, mutable array and mutates it in place on drop (the `:list` pattern); binding a
      // computed or the prop directly breaks its drop reconciliation ("index of null").
      internalList: [],
    };
  },

  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        this.internalList = [...(value || [])];
      },
    },
  },

  methods: {
    onListChange() {
      // vuedraggable has already reordered internalList in place; publish the new order.
      this.$emit('update:modelValue', [...this.internalList]);
    },

    onPointerDownCapture(event) {
      // Runs before SortableJS's own pointerdown handler (capture phase, on an ancestor), so the
      // delay we set here is the one SortableJS reads for this gesture: 0 on the handle (immediate),
      // longPressDelay on the body (hold-to-drag). delayOnTouchOnly means this only bites on touch.
      const listEl = event.currentTarget.querySelector('.hr-sortable-list');
      const sortable = listEl ? Sortable.get(listEl) : null;
      if (!sortable) {
        return;
      }
      const onHandle = event.target instanceof Element
        && event.target.closest(this.handleSelector) !== null;
      sortable.option('delay', onHandle ? 0 : this.longPressDelay);
    },
  },

  render() {
    const dragConfig = this.longPressBody
      ? {
        delay: this.longPressDelay,
        delayOnTouchOnly: true,
        touchStartThreshold: 5,
        fallbackTolerance: 5,
      }
      : { handle: this.handleSelector };

    const list = h(
      draggable,
      {
        class: 'hr-sortable-list',
        list: this.internalList,
        itemKey: this.itemKey,
        disabled: this.disabled,
        animation: this.animation,
        forceFallback: true,
        ghostClass: 'hr-sortable-list__ghost',
        chosenClass: 'hr-sortable-list__chosen',
        fallbackClass: 'hr-sortable-list__fallback',
        tag: 'div',
        onChange: this.onListChange,
        ...dragConfig,
      },
      // Forward the consumer's item slot by reference — no intermediate outlet.
      { item: this.$slots.item },
    );

    if (!this.longPressBody) {
      return list;
    }

    // Ancestor wrapper carrying the capture-phase listener (see onPointerDownCapture).
    return h(
      'div',
      { class: 'hr-sortable-list__wrap', onPointerdownCapture: this.onPointerDownCapture },
      [list],
    );
  },
};
</script>

<style lang="scss" scoped>
.hr-sortable-list {
  display: flex;
  flex-direction: column;

  /*
   * Rows aren't selectable text: on touch, a long-press would otherwise start a text selection
   * (and the iOS selection callout) and fight the long-press-to-drag pickup.
   */
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}
</style>

<style lang="scss">
/**
 * The handle lives in consumer-provided slot markup, and the ghost/fallback elements are cloned
 * during drag — none are reachable by scoped styles, so these are intentionally global.
 */
.hr-sortable-list__handle {
  cursor: grab;
}

.hr-sortable-list__handle:active {
  cursor: grabbing;
}

/* The gap left where the row will drop. */
.hr-sortable-list__ghost {
  opacity: 0.4;
}

/*
 * Raised the moment the row is picked up — immediately on handle press, or right after the
 * long-press completes on the body — and kept raised on the clone that follows the pointer.
 * `chosen` is applied by SortableJS at pickup, before any movement; `:not(__ghost)` drops the
 * elevation off the source once it becomes the flat drop-gap placeholder during the drag.
 */
.hr-sortable-list__chosen:not(.hr-sortable-list__ghost),
.hr-sortable-list__fallback {
  box-shadow: var(--app-elevation-3);
  cursor: grabbing;
}
</style>
